import { useState, useEffect, useCallback, useMemo } from 'react'
import { db } from '../lib/supabase'

export function useTodos(businessId = null) {
  const [todos, setTodos] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [subtaskLoading, setSubtaskLoading] = useState(false)
  const [linkLoading, setLinkLoading] = useState(false)

  const fetchTodos = useCallback(async () => {
    setLoading(true)
    const { data, error } = await db.getTodos(businessId)
    if (error) {
      setError(error)
    } else {
      setTodos(data)
    }
    setLoading(false)
  }, [businessId])

  useEffect(() => {
    fetchTodos()
  }, [fetchTodos])

  const createTodo = async (todo) => {
    // Extract local subtasks/links before creating
    const { _localSubtasks, _localLinks, ...todoData } = todo

    const { data, error } = await db.createTodo(todoData)
    if (!error && data) {
      // Create subtasks if any
      if (_localSubtasks && _localSubtasks.length > 0) {
        for (const subtask of _localSubtasks) {
          await db.createSubtask(data.id, subtask.title, subtask.assigned_to)
        }
      }
      // Create links if any
      if (_localLinks && _localLinks.length > 0) {
        for (const link of _localLinks) {
          await db.createTodoLink(data.id, link.url, link.title)
        }
      }
      // Refetch to get the complete data with subtasks/links
      fetchTodos()
    }
    return { data, error }
  }

  const updateTodo = async (id, updates) => {
    const { data, error } = await db.updateTodo(id, updates)
    if (!error && data) {
      setTodos(prev => prev.map(t => t.id === id ? data : t))
    }
    return { data, error }
  }

  const deleteTodo = async (id) => {
    const { error } = await db.deleteTodo(id)
    if (!error) {
      setTodos(prev => prev.filter(t => t.id !== id))
    }
    return { error }
  }

  const toggleComplete = async (id) => {
    const todo = todos.find(t => t.id === id)
    if (!todo) return { error: 'Todo not found' }

    const newStatus = todo.status === 'completed' ? 'pending' : 'completed'
    const updates = {
      status: newStatus,
      completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
    }

    return updateTodo(id, updates)
  }

  // ============================================
  // SUBTASK OPERATIONS
  // ============================================

  const addSubtask = async (todoId, title, assignedTo = null) => {
    setSubtaskLoading(true)
    const { data, error } = await db.createSubtask(todoId, title, assignedTo)
    if (!error && data) {
      setTodos(prev => prev.map(t => {
        if (t.id !== todoId) return t
        return {
          ...t,
          todo_subtasks: [...(t.todo_subtasks || []), data],
          subtask_count: (t.subtask_count || 0) + 1,
        }
      }))
    }
    setSubtaskLoading(false)
    return { data, error }
  }

  const toggleSubtask = async (subtaskId) => {
    setSubtaskLoading(true)
    const { data, error } = await db.toggleSubtask(subtaskId)
    if (!error && data) {
      setTodos(prev => prev.map(t => {
        const subtaskIndex = t.todo_subtasks?.findIndex(s => s.id === subtaskId)
        if (subtaskIndex === -1 || subtaskIndex === undefined) return t

        const updatedSubtasks = [...t.todo_subtasks]
        updatedSubtasks[subtaskIndex] = data

        return {
          ...t,
          todo_subtasks: updatedSubtasks,
          subtask_completed_count: updatedSubtasks.filter(s => s.is_completed).length,
        }
      }))
    }
    setSubtaskLoading(false)
    return { data, error }
  }

  const deleteSubtask = async (subtaskId) => {
    setSubtaskLoading(true)
    const { error } = await db.deleteSubtask(subtaskId)
    if (!error) {
      setTodos(prev => prev.map(t => {
        const subtasks = t.todo_subtasks || []
        const filtered = subtasks.filter(s => s.id !== subtaskId)
        if (filtered.length === subtasks.length) return t

        return {
          ...t,
          todo_subtasks: filtered,
          subtask_count: filtered.length,
          subtask_completed_count: filtered.filter(s => s.is_completed).length,
        }
      }))
    }
    setSubtaskLoading(false)
    return { error }
  }

  // ============================================
  // LINK OPERATIONS
  // ============================================

  const addLink = async (todoId, url, title = null) => {
    setLinkLoading(true)
    const { data, error } = await db.createTodoLink(todoId, url, title)
    if (!error && data) {
      setTodos(prev => prev.map(t => {
        if (t.id !== todoId) return t
        return {
          ...t,
          todo_links: [...(t.todo_links || []), data],
          link_count: (t.link_count || 0) + 1,
        }
      }))
    }
    setLinkLoading(false)
    return { data, error }
  }

  const deleteLink = async (linkId) => {
    setLinkLoading(true)
    const { error } = await db.deleteTodoLink(linkId)
    if (!error) {
      setTodos(prev => prev.map(t => {
        const links = t.todo_links || []
        const filtered = links.filter(l => l.id !== linkId)
        if (filtered.length === links.length) return t

        return {
          ...t,
          todo_links: filtered,
          link_count: filtered.length,
        }
      }))
    }
    setLinkLoading(false)
    return { error }
  }

  // Group todos by status
  const grouped = useMemo(() => ({
    pending: todos.filter(t => t.status === 'pending'),
    in_progress: todos.filter(t => t.status === 'in_progress'),
    completed: todos.filter(t => t.status === 'completed'),
  }), [todos])

  // Get counts
  const counts = useMemo(() => ({
    total: todos.length,
    pending: grouped.pending.length,
    in_progress: grouped.in_progress.length,
    completed: grouped.completed.length,
  }), [todos, grouped])

  return {
    todos,
    grouped,
    counts,
    loading,
    error,
    refetch: fetchTodos,
    createTodo,
    updateTodo,
    deleteTodo,
    toggleComplete,
    // Subtask operations
    addSubtask,
    toggleSubtask,
    deleteSubtask,
    subtaskLoading,
    // Link operations
    addLink,
    deleteLink,
    linkLoading,
  }
}

export default useTodos
